import type { AudiencePoint } from '@crosscastr/shared';

const W = 860;
const H = 250;
const PAD_X = 28;
const TOP = 26;
const BOT = 214;

const HE_MONTHS = ['ינו', 'פבר', 'מרץ', 'אפר', 'מאי', 'יוני', 'יולי', 'אוג', 'ספט', 'אוק', 'נוב', 'דצמ'];

/**
 * Audience-growth area chart (DESIGN.md §5). Inline SVG, hairline grid, gradient area fill, last
 * point emphasised. RTL time axis: right = oldest → left = newest (DESIGN.md §4). Data-driven
 * from the real /api/dashboard series.
 */
export function GrowthChart({ series }: { series: AudiencePoint[] }): JSX.Element {
  if (series.length < 2) return <div className="empty">אין עדיין נתוני צמיחה</div>;

  const values = series.map((p) => p.members);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const n = series.length;

  // RTL: index 0 (oldest) → right edge; last (newest) → left edge.
  const xAt = (i: number) => W - PAD_X - (i / (n - 1)) * (W - 2 * PAD_X);
  const yAt = (v: number) => TOP + (1 - (v - min) / span) * (BOT - TOP);

  const linePts = series.map((p, i) => `${xAt(i).toFixed(1)},${yAt(p.members).toFixed(1)}`);
  const linePath = `M${linePts.join(' L')}`;
  const areaPath = `${linePath} L${xAt(n - 1).toFixed(1)},${H} L${xAt(0).toFixed(1)},${H} Z`;

  const newestX = xAt(n - 1);
  const newestY = yAt(series[n - 1].members);

  // Four month labels across the axis (right → left = old → new).
  const labelIdx = [0, Math.floor(n / 3), Math.floor((2 * n) / 3), n - 1];
  const labels = labelIdx.map((i) => HE_MONTHS[new Date(series[i].date).getMonth()]);

  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="גרף צמיחת קהל">
        <defs>
          <linearGradient id="ccArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--accent-teal)" stopOpacity="0.34" />
            <stop offset="1" stopColor="var(--accent-teal)" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="ccStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="var(--green-500)" />
            <stop offset="1" stopColor="var(--teal-500)" />
          </linearGradient>
        </defs>
        <g stroke="var(--grid)" strokeWidth="1">
          {[40, 95, 150, 205].map((y) => (
            <line key={y} x1="0" y1={y} x2={W} y2={y} />
          ))}
        </g>
        <path d={areaPath} fill="url(#ccArea)" />
        <path d={linePath} fill="none" stroke="url(#ccStroke)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        <circle className="chart-now" cx={newestX} cy={newestY} r="5.5" fill="var(--green-500)" stroke="var(--surface-2)" strokeWidth="2.5" />
        <circle cx={newestX} cy={newestY} r="11" fill="none" stroke="var(--green-500)" strokeOpacity="0.35" strokeWidth="1.5" />
      </svg>
      <div className="chart-axis">
        {labels.map((m, i) => (
          <span key={i}>{m}</span>
        ))}
      </div>
    </div>
  );
}
